import React, {
  createContext, useContext, useState, useEffect, useMemo, useCallback
} from 'react';
import {
  db, collection, addDoc, doc, updateDoc, deleteDoc, query, where,
  onSnapshot, serverTimestamp, writeBatch
} from '../firebase/config';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---------- Load user's notifications (real-time) ----------
  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return bT - aT;
      });
      setNotifications(data);
      setLoading(false);
    }, (err) => {
      console.error('Notifications snapshot:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [user?.uid]);

  // ---------- Derived ----------
  const unreadCount = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  // ---------- Mutations ----------

  /**
   * Legacy API — adds a notification in-memory for the current user.
   * Still supported for backward compat, but writes to Firestore.
   */
  const addNotification = useCallback(async (notification) => {
    if (!user?.uid) return null;
    try {
      const ref = await addDoc(collection(db, 'notifications'), {
        userId: user.uid,
        type: notification.type || 'system',
        title: notification.title || 'Notification',
        body: notification.body || notification.message || '',
        link: notification.link || null,
        metadata: notification.metadata || {},
        read: false,
        createdAt: serverTimestamp(),
      });
      return ref.id;
    } catch (err) {
      console.error('addNotification failed:', err);
      return null;
    }
  }, [user?.uid]);

  const markAsRead = useCallback(async (id) => {
    if (!id) return;
    // Optimistic update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      await updateDoc(doc(db, 'notifications', id), {
        read: true,
        readAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('markAsRead failed:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.read);
    if (unread.length === 0) return;
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      const batch = writeBatch(db);
      unread.forEach(n => {
        batch.update(doc(db, 'notifications', n.id), {
          read: true,
          readAt: serverTimestamp(),
        });
      });
      await batch.commit();
    } catch (err) {
      console.error('markAllAsRead failed:', err);
    }
  }, [notifications]);

  const deleteNotification = useCallback(async (id) => {
    if (!id) return;
    // Optimistic update
    setNotifications(prev => prev.filter(n => n.id !== id));
    try {
      await deleteDoc(doc(db, 'notifications', id));
    } catch (err) {
      console.error('deleteNotification failed:', err);
    }
  }, []);

  /**
   * Legacy method — clears all notifications for current user.
   * (Renamed internally, but kept for compat.)
   */
  const clearNotifications = useCallback(async () => {
    if (notifications.length === 0) return;
    const toDelete = [...notifications];
    setNotifications([]);
    try {
      const batch = writeBatch(db);
      toDelete.forEach(n => {
        batch.delete(doc(db, 'notifications', n.id));
      });
      await batch.commit();
    } catch (err) {
      console.error('clearNotifications failed:', err);
      // Restore on failure
      setNotifications(toDelete);
    }
  }, [notifications]);

  // Alias for the new naming convention
  const clearAll = clearNotifications;

  const value = useMemo(() => ({
    // State
    notifications,
    unreadCount,
    loading,

    // New API
    deleteNotification,
    clearAll,

    // Legacy-compatible API (existing code depends on these names)
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotifications,
  }), [
    notifications, unreadCount, loading,
    addNotification, markAsRead, markAllAsRead,
    deleteNotification, clearNotifications, clearAll,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Legacy hook name (your old code uses this)
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// New hook name (used by NotificationBell)
export const useNotifications = useNotification;